import React, { useRef, useState, useEffect, useCallback } from 'react';

const SUBTITLE_LANGS = [
  { code: 'fr', label: 'FR' },
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
  { code: 'pt', label: 'PT' },
];

const BobineVideoPlayer = ({ src, sourceSrc, sourceType, style = {}, autoPlay = false, testId = 'bobine-video', subtitleBaseUrl }) => {
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(autoPlay);
  const [currentTime, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [activeLang, setActiveLang] = useState(null);
  const [currentCue, setCurrentCue] = useState('');
  const hideTimer = useRef(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const onFS = () => {
      const inFS = !!document.fullscreenElement || !!document.webkitFullscreenElement;
      setIsFullscreen(inFS);
      if (!inFS) {
        try { screen.orientation?.unlock?.(); } catch {}
        setShowControls(true);
      }
    };
    document.addEventListener('fullscreenchange', onFS);
    document.addEventListener('webkitfullscreenchange', onFS);
    return () => {
      document.removeEventListener('fullscreenchange', onFS);
      document.removeEventListener('webkitfullscreenchange', onFS);
      // Cleanup: exit fullscreen if component unmounts while in fullscreen
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        try { document.exitFullscreen?.(); } catch {}
        try { document.webkitExitFullscreen?.(); } catch {}
      }
      try { screen.orientation?.unlock?.(); } catch {}
    };
  }, []);

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  }, []);

  const seek = useCallback((e) => {
    const v = videoRef.current;
    const bar = progressRef.current;
    if (!v || !bar || !v.duration) return;
    const rect = bar.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    v.currentTime = pct * v.duration;
  }, []);

  const seekBy = useCallback((seconds) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = Math.max(0, Math.min(v.duration, v.currentTime + seconds));
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }, []);

  const goFullscreen = useCallback(() => {
    const container = containerRef.current;
    const v = videoRef.current;
    if (!container && !v) return;
    try {
      const enterFS = () => {
        try { screen.orientation?.lock?.('landscape').catch(() => {}); } catch {}
      };
      if (container?.requestFullscreen) { container.requestFullscreen().then(enterFS).catch(() => {
        if (v?.webkitEnterFullscreen) { v.webkitEnterFullscreen(); enterFS(); }
      }); }
      else if (container?.webkitRequestFullscreen) { container.webkitRequestFullscreen(); enterFS(); }
      else if (v?.webkitEnterFullscreen) { v.webkitEnterFullscreen(); enterFS(); }
      else if (v?.requestFullscreen) { v.requestFullscreen().then(enterFS).catch(() => {}); }
    } catch {}
  }, []);

  const exitFullscreen = useCallback(() => {
    try {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
      else if (document.webkitFullscreenElement) document.webkitExitFullscreen();
      try { screen.orientation?.unlock?.(); } catch {}
    } catch {}
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) exitFullscreen();
    else goFullscreen();
  }, [isFullscreen, exitFullscreen, goFullscreen]);

  const resetHideTimer = useCallback(() => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => { if (playing) setShowControls(false); }, 3000);
  }, [playing]);

  /* ═══ Gestion des pistes de sous-titres natives ═══ */
  const toggleSubtitle = useCallback((langCode) => {
    const v = videoRef.current;
    if (!v) return;
    if (activeLang === langCode) {
      for (let i = 0; i < v.textTracks.length; i++) v.textTracks[i].mode = 'disabled';
      setActiveLang(null);
      setCurrentCue('');
    } else {
      for (let i = 0; i < v.textTracks.length; i++) {
        if (v.textTracks[i].language === langCode) {
          v.textTracks[i].mode = (isMobile && !isFullscreen) ? 'hidden' : 'showing';
        } else {
          v.textTracks[i].mode = 'disabled';
        }
      }
      setActiveLang(langCode);
    }
  }, [activeLang, isMobile, isFullscreen]);

  /* Basculer mode track quand on entre/sort du plein ecran */
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !activeLang) return;
    for (let i = 0; i < v.textTracks.length; i++) {
      if (v.textTracks[i].language === activeLang) {
        v.textTracks[i].mode = (isMobile && !isFullscreen) ? 'hidden' : 'showing';
      }
    }
  }, [isMobile, isFullscreen, activeLang]);

  /* Desactiver toutes les pistes au demarrage */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const initTracks = () => {
      for (let i = 0; i < v.textTracks.length; i++) v.textTracks[i].mode = 'disabled';
    };
    initTracks();
    v.addEventListener('loadedmetadata', initTracks);
    return () => v.removeEventListener('loadedmetadata', initTracks);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      setCurrent(v.currentTime);
      if (isMobile && activeLang && !isFullscreen) {
        const track = Array.from(v.textTracks).find(t => t.language === activeLang);
        if (track && track.activeCues && track.activeCues.length > 0) {
          setCurrentCue(track.activeCues[0].text);
        } else {
          setCurrentCue('');
        }
      }
    };
    const onMeta = () => setDuration(v.duration);
    const onEnd = () => { setPlaying(false); setShowControls(true); };
    const onPlay = () => setPlaying(true);
    const onPause = () => { setPlaying(false); setShowControls(true); };
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('loadedmetadata', onMeta);
    v.addEventListener('ended', onEnd);
    v.addEventListener('play', onPlay);
    v.addEventListener('pause', onPause);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('loadedmetadata', onMeta);
      v.removeEventListener('ended', onEnd);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('pause', onPause);
    };
  }, [isMobile, activeLang, isFullscreen]);

  useEffect(() => { resetHideTimer(); }, [playing, resetHideTimer]);

  const pct = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;500&display=swap');
        @keyframes shimmerGoldSub {
          0% { text-shadow: 0 0 8px rgba(212,175,55,0.3), 0 0 20px rgba(212,175,55,0.1); }
          50% { text-shadow: 0 0 14px rgba(212,175,55,0.5), 0 0 30px rgba(212,175,55,0.2); }
          100% { text-shadow: 0 0 8px rgba(212,175,55,0.3), 0 0 20px rgba(212,175,55,0.1); }
        }
        @keyframes fadeInCue { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        video::cue {
          font-family: 'Dancing Script', cursive;
          color: #D4AF37;
          background-color: rgba(10, 10, 10, 0.75);
          font-size: 1.2em;
          line-height: 1.5;
        }
        [data-testid="${testId}"] div:fullscreen,
        [data-testid="${testId}"] div:-webkit-full-screen {
          width: 100vw; height: 100vh; background: #000;
        }
        [data-testid="${testId}"] div:fullscreen video,
        [data-testid="${testId}"] div:-webkit-full-screen video {
          width: 100%; height: 100%; object-fit: contain;
        }
      `}</style>

      {/* Zone video */}
      <div
        ref={containerRef}
        onMouseMove={resetHideTimer}
        onMouseLeave={() => { if (playing) setShowControls(false); }}
        onClick={togglePlay}
        onContextMenu={e => e.preventDefault()}
        style={{
          position: 'relative', borderRadius: isFullscreen ? 0 : 12, overflow: 'hidden',
          backgroundColor: '#000', cursor: 'pointer', height: '100%', ...style,
        }}
      >
        <video
          ref={videoRef}
          preload="metadata"
          playsInline
          disablePictureInPicture
          disableRemotePlayback
          autoPlay={autoPlay}
          onContextMenu={e => e.preventDefault()}
          style={{ width: '100%', height: '100%', display: 'block', borderRadius: 12, objectFit: isMobile ? 'contain' : 'cover' }}
        >
          {sourceSrc ? <source src={sourceSrc} type={sourceType || 'video/mp4'} /> : null}
          {src && !sourceSrc ? <source src={src} type="video/mp4" /> : null}
          {subtitleBaseUrl && SUBTITLE_LANGS.map(lang => (
            <track
              key={lang.code}
              kind="subtitles"
              srcLang={lang.code}
              label={lang.label}
              src={`${subtitleBaseUrl}?lang=${lang.code}`}
            />
          ))}
        </video>

        {/* Overlay play central quand en pause */}
        {!playing && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.3)', transition: 'opacity 0.3s',
          }}>
            <div style={{
              width: 60, height: 60, borderRadius: '50%',
              backgroundColor: 'rgba(212,175,55,0.15)', border: '2px solid rgba(212,175,55,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backdropFilter: 'blur(8px)',
              boxShadow: '0 0 30px rgba(212,175,55,0.2)',
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="#D4AF37" stroke="none">
                <polygon points="8 5 20 12 8 19 8 5"/>
              </svg>
            </div>
          </div>
        )}

        {/* Bouton Son Mobile — haut gauche */}
        {isMobile && (
          <button
            data-testid={`${testId}-mobile-mute`}
            onClick={(e) => { e.stopPropagation(); toggleMute(); }}
            style={{
              position: 'absolute', top: 8, left: 8, zIndex: 10,
              width: 36, height: 36, borderRadius: 8,
              backgroundColor: 'rgba(0,0,0,0.5)', border: '1px solid rgba(212,175,55,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', backdropFilter: 'blur(4px)',
            }}
          >
            {muted ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 010 14.14"/><path d="M15.54 8.46a5 5 0 010 7.07"/></svg>
            )}
          </button>
        )}

        {/* Bouton Plein Ecran Mobile — haut droite */}
        {isMobile && (
          <button
            data-testid={`${testId}-mobile-fullscreen`}
            onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
            style={{
              position: 'absolute', top: 8, right: 8, zIndex: 10,
              width: 36, height: 36, borderRadius: 8,
              backgroundColor: 'rgba(0,0,0,0.5)', border: '1px solid rgba(212,175,55,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', backdropFilter: 'blur(4px)',
            }}
          >
            {isFullscreen ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round">
                <path d="M4 14h6v6m10-10h-6V4M4 10h6V4m10 10h-6v6"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round">
                <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/>
              </svg>
            )}
          </button>
        )}

        {/* Bouton X de sortie plein écran — toujours visible en fullscreen */}
        {isFullscreen && (
          <button
            data-testid={`${testId}-exit-fullscreen`}
            onClick={(e) => { e.stopPropagation(); exitFullscreen(); }}
            style={{
              position: 'absolute', top: 12, right: 12, zIndex: 20,
              width: 44, height: 44, borderRadius: '50%',
              backgroundColor: 'rgba(0,0,0,0.7)', border: '2px solid #D4AF37',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', backdropFilter: 'blur(4px)',
              boxShadow: '0 0 16px rgba(212,175,55,0.3)',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}

        {/* Barre de controles Heritage */}
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: 'linear-gradient(transparent, rgba(0,0,0,0.85))',
            padding: '20px 14px 10px', display: 'flex', flexDirection: 'column', gap: 6,
            opacity: showControls ? 1 : 0, transition: 'opacity 0.4s ease',
            pointerEvents: showControls ? 'auto' : 'none',
          }}
        >
          <div
            ref={progressRef}
            onClick={seek}
            onTouchStart={(e) => { e.stopPropagation(); seek(e); }}
            onTouchMove={(e) => { e.stopPropagation(); seek(e); }}
            style={{ width: '100%', height: isMobile ? 20 : 4, backgroundColor: 'rgba(212,175,55,0.15)', borderRadius: 2, cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center' }}
          >
            <div style={{ position: 'absolute', top: isMobile ? 8 : 0, left: 0, right: 0, height: 4, borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                width: `${pct}%`, height: '100%', borderRadius: 2,
                background: 'linear-gradient(90deg, #B8942E, #D4AF37, #F5D778)',
                transition: 'width 0.1s linear',
              }} />
            </div>
            <div style={{
              position: 'absolute', top: isMobile ? 4 : -4, left: `${pct}%`, transform: 'translateX(-50%)',
              width: isMobile ? 16 : 12, height: isMobile ? 16 : 12, borderRadius: '50%',
              background: 'linear-gradient(145deg, #D4AF37, #B8860B)', border: '2px solid #050502',
              boxShadow: '0 0 8px rgba(212,175,55,0.4)',
              opacity: showControls ? 1 : 0, transition: 'opacity 0.2s',
            }} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 12 }}>
            {/* Rewind -10s */}
            <button data-testid={`${testId}-rewind-btn`} onClick={(e) => { e.stopPropagation(); seekBy(-10); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', position: 'relative' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
              </svg>
              <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 6, fontWeight: 700, color: '#D4AF37', fontFamily: "'Georgia', serif" }}>10</span>
            </button>

            <button data-testid={`${testId}-play-btn`} onClick={togglePlay} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
              {playing ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#D4AF37" stroke="none"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#D4AF37" stroke="none"><polygon points="6 3 20 12 6 21 6 3"/></svg>
              )}
            </button>

            {/* Forward +10s */}
            <button data-testid={`${testId}-forward-btn`} onClick={(e) => { e.stopPropagation(); seekBy(10); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', position: 'relative' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.13-9.36L23 10"/>
              </svg>
              <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: 6, fontWeight: 700, color: '#D4AF37', fontFamily: "'Georgia', serif" }}>10</span>
            </button>

            <span style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37', letterSpacing: 0.5 }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>

            <div style={{ flex: 1 }} />

            <button data-testid={`${testId}-mute-btn`} onClick={toggleMute} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
              {muted ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 010 14.14"/><path d="M15.54 8.46a5 5 0 010 7.07"/></svg>
              )}
            </button>

            <button data-testid={`${testId}-fullscreen-btn`} onClick={(e) => {
              e.stopPropagation();
              toggleFullscreen();
            }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}>
              {isFullscreen ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 14h6v6m10-10h-6V4M4 10h6V4m10 10h-6v6"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round">
                  <path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/>
                </svg>
              )}
            </button>
          </div>

          {/* Subtitle language row — separate to avoid overflow */}
          {subtitleBaseUrl && (
            <div data-testid={`${testId}-subtitle-langs`} style={{ display: 'flex', gap: 4, alignItems: 'center', justifyContent: 'center', paddingTop: 2 }}>
              {SUBTITLE_LANGS.map(lang => (
                <button
                  key={lang.code}
                  data-testid={`${testId}-sub-${lang.code}`}
                  onClick={(e) => { e.stopPropagation(); toggleSubtitle(lang.code); }}
                  style={{
                    fontFamily: "'Georgia', serif", fontSize: 9, fontWeight: 700,
                    padding: '3px 8px', borderRadius: 3, cursor: 'pointer',
                    letterSpacing: 0.5, transition: 'all 0.2s',
                    border: activeLang === lang.code ? '1px solid #D4AF37' : '1px solid rgba(212,175,55,0.3)',
                    backgroundColor: activeLang === lang.code ? 'rgba(212,175,55,0.25)' : 'rgba(0,0,0,0.4)',
                    color: activeLang === lang.code ? '#D4AF37' : '#D4AF37',
                  }}
                >
                  {lang.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sous-titres Mobile — EXIL DANS LE NOIR, sous la video */}
      {isMobile && !isFullscreen && (
        <div
          data-testid={`${testId}-mobile-cue`}
          style={{
            minHeight: 44, display: 'flex', justifyContent: 'center', alignItems: 'center',
            padding: '8px 10px 4px',
          }}
        >
          {currentCue && (
            <span style={{
              fontFamily: "'Dancing Script', 'Brush Script MT', 'Segoe Script', cursive",
              fontSize: 18, fontWeight: 500, lineHeight: 1.4,
              color: '#D4AF37', textAlign: 'center', maxWidth: '95%',
              animation: 'shimmerGoldSub 3s ease-in-out infinite, fadeInCue 0.4s ease-out',
              letterSpacing: 0.3,
            }}>
              {currentCue}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default BobineVideoPlayer;
